/**
 * Selection Info Panel - displays information about selected units/buildings
 */

import { useGameStore } from '@/state/gameStore';
import { FACTIONS, UNIT_STATS, BUILDING_STATS } from '@/game/factions/FactionData';
import type { World } from '@/core/ecs/World';
import { ComponentTypes } from '@/core/ecs/Component';
import type { HealthComponent } from '@/game/components/Health';
import type { FactionComponent } from '@/game/components/Faction';
import type { UnitComponent } from '@/game/components/Unit';
import { type BuildingComponent, MAX_BUILDING_LEVEL, getUpgradeCost, getRepairCost, getBuildingEfficiency } from '@/game/components/Building';
import type { Entity } from '@/types';
import { EventBus } from '@/core/events/EventBus';
import { UI_IMAGES } from '@/ui/styles/UIIcons';

export class SelectionInfo {
  private container: HTMLElement;
  private world: World | null = null;
  private unsubscribe: (() => void) | null = null;
  private refreshInterval: number | null = null;

  constructor() {
    this.container = document.createElement('div');
    this.container.id = 'selection-info';
    this.container.innerHTML = `
      <style>
        #selection-info {
          position: fixed;
          left: 16px;
          top: 50%;
          transform: translateY(-50%);
          z-index: 25;
          font: 13px/1.4 'Noto Serif SC', 'SimSun', serif;
          color: #f5e6d3;
          user-select: none;
          width: 200px;
          display: none;
          pointer-events: auto;
        }
        #selection-info.visible {
          display: block;
        }

        /* Panel wrapper */
        #selection-info .panel-wrapper {
          background: linear-gradient(145deg, rgba(74,55,40,0.95) 0%, rgba(45,31,21,0.98) 100%);
          border: 3px solid #8b6914;
          border-radius: 8px;
          box-shadow:
            inset 0 2px 4px rgba(255,220,150,0.15),
            inset 0 -3px 6px rgba(0,0,0,0.4),
            0 6px 20px rgba(0,0,0,0.6);
          overflow: hidden;
          position: relative;
        }

        /* Corner ornaments */
        #selection-info .corner {
          position: absolute;
          width: 24px;
          height: 24px;
          background-image: url('/images/ui/decorations/corner-ornaments.png');
          background-size: 200% 200%;
          z-index: 3;
          filter: drop-shadow(0 2px 3px rgba(0,0,0,0.4));
          pointer-events: none;
        }
        #selection-info .corner.tl { top: -4px; left: -4px; background-position: 0% 0%; }
        #selection-info .corner.tr { top: -4px; right: -4px; background-position: 100% 0%; }
        #selection-info .corner.bl { bottom: -4px; left: -4px; background-position: 0% 100%; }
        #selection-info .corner.br { bottom: -4px; right: -4px; background-position: 100% 100%; }

        /* Title bar */
        #selection-info .panel-title {
          background: linear-gradient(180deg, rgba(107,68,35,0.9) 0%, rgba(74,47,23,0.95) 100%);
          border-bottom: 2px solid #8b6914;
          padding: 6px 10px;
          text-align: center;
          position: relative;
        }
        #selection-info .panel-title::after {
          content: '';
          position: absolute;
          bottom: -2px;
          left: 15px;
          right: 15px;
          height: 1px;
          background: linear-gradient(90deg, transparent 0%, rgba(212,175,55,0.6) 50%, transparent 100%);
        }
        #selection-info .panel-title-text {
          font: 700 12px/1 'Noto Serif SC', 'SimSun', serif;
          color: #ffd35a;
          text-shadow: 0 1px 2px rgba(0,0,0,0.5);
          letter-spacing: 2px;
        }

        /* Content area */
        #selection-info .panel-content {
          padding: 10px;
          background: linear-gradient(to bottom, rgba(45,31,21,0.85) 0%, rgba(45,31,21,0.9) 100%);
        }

        /* Header with icon and name */
        #selection-info .info-header {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 10px;
          padding-bottom: 8px;
          border-bottom: 1px solid rgba(139,105,20,0.4);
        }
        #selection-info .info-icon {
          width: 48px;
          height: 48px;
          background: linear-gradient(180deg, #5c4033 0%, #3d2a1c 100%);
          border: 2px solid #8b6914;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          overflow: hidden;
        }
        #selection-info .info-icon img {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }
        #selection-info .info-title {
          flex: 1;
          min-width: 0;
        }
        #selection-info .info-name {
          font: 700 14px 'Noto Serif SC', 'SimSun', serif;
          color: #ffd35a;
          text-shadow: 0 1px 2px rgba(0,0,0,0.5);
          margin-bottom: 2px;
        }
        #selection-info .info-faction {
          font-size: 11px;
          color: #c4a77d;
        }

        /* Health section */
        #selection-info .health-section {
          margin-bottom: 10px;
        }
        #selection-info .health-label {
          font-size: 10px;
          color: #9e8b6e;
          margin-bottom: 3px;
          display: flex;
          justify-content: space-between;
        }
        #selection-info .health-bar {
          height: 8px;
          background: linear-gradient(180deg, #2d1f15 0%, #1a1209 100%);
          border: 1px solid #5c4033;
          border-radius: 4px;
          overflow: hidden;
        }
        #selection-info .health-bar-fill {
          height: 100%;
          background: linear-gradient(180deg, #6b8e23 0%, #4a6b18 100%);
          transition: width 0.3s ease;
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.2);
        }
        #selection-info .health-bar-fill.low {
          background: linear-gradient(180deg, #cd5c5c 0%, #8b3a3a 100%);
        }

        /* Info row */
        #selection-info .info-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 6px 8px;
          background: linear-gradient(180deg, #4a3728 0%, #2d1f15 100%);
          border: 1px solid #5c4033;
          border-radius: 4px;
          margin-bottom: 6px;
        }
        #selection-info .info-row-label {
          font-size: 11px;
          color: #9e8b6e;
        }
        #selection-info .info-row-value {
          font: 700 12px 'Noto Serif SC', 'SimSun', serif;
          color: #ffd35a;
        }
        #selection-info .info-row-preview {
          font-size: 10px;
          color: #6b8e23;
        }

        /* Production info */
        #selection-info .production-info {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px;
          background: linear-gradient(180deg, #4a3728 0%, #2d1f15 100%);
          border: 1px solid #5c4033;
          border-radius: 4px;
          margin-bottom: 8px;
        }
        #selection-info .production-icon {
          width: 32px;
          height: 32px;
          background: linear-gradient(180deg, #5c4033 0%, #3d2a1c 100%);
          border: 1px solid #6b4e0f;
          border-radius: 4px;
          overflow: hidden;
        }
        #selection-info .production-icon img {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }
        #selection-info .production-details {
          flex: 1;
        }
        #selection-info .production-name {
          font: 700 11px 'Noto Serif SC', 'SimSun', serif;
          color: #c4a77d;
        }
        #selection-info .production-time {
          font-size: 10px;
          color: #9e8b6e;
        }

        /* Upgrade preview */
        #selection-info .upgrade-preview {
          padding: 8px;
          background: linear-gradient(180deg, rgba(107,78,15,0.3) 0%, rgba(74,55,40,0.3) 100%);
          border: 1px dashed #8b6914;
          border-radius: 4px;
          margin-bottom: 8px;
        }
        #selection-info .upgrade-preview-title {
          font-size: 10px;
          color: #d4af37;
          margin-bottom: 4px;
          text-align: center;
        }
        #selection-info .upgrade-preview-row {
          display: flex;
          justify-content: space-between;
          font-size: 10px;
          color: #c4a77d;
        }
        #selection-info .upgrade-preview-new {
          color: #6b8e23;
        }

        /* Action buttons */
        #selection-info .action-buttons {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        #selection-info .btn {
          padding: 8px 10px;
          border: 2px solid;
          border-radius: 4px;
          font: 700 11px 'Noto Serif SC', 'SimSun', serif;
          cursor: pointer;
          width: 100%;
          transition: all 0.2s;
          text-shadow: 0 1px 2px rgba(0,0,0,0.5);
          text-align: center;
        }
        #selection-info .btn-upgrade {
          background: linear-gradient(180deg, #8b6914 0%, #6b4e0f 100%);
          border-color: #d4af37;
          color: #ffd35a;
        }
        #selection-info .btn-upgrade:hover:not(:disabled) {
          background: linear-gradient(180deg, #a07a18 0%, #7d5b12 100%);
          box-shadow: 0 0 10px rgba(212,175,55,0.4);
        }
        #selection-info .btn-repair {
          background: linear-gradient(180deg, #4a6b18 0%, #365210 100%);
          border-color: #6b8e23;
          color: #b8d468;
        }
        #selection-info .btn-repair:hover:not(:disabled) {
          background: linear-gradient(180deg, #5a7d1e 0%, #426314 100%);
          box-shadow: 0 0 10px rgba(107,142,35,0.4);
        }
        #selection-info .btn:disabled {
          background: linear-gradient(180deg, #4a3728 0%, #2d1f15 100%);
          border-color: #5c4033;
          color: #6b5a45;
          cursor: not-allowed;
          box-shadow: none;
        }

        /* Multi unit selection */
        #selection-info .unit-list {
          display: flex;
          flex-direction: column;
          gap: 4px;
          margin-bottom: 8px;
        }
        #selection-info .unit-item {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 4px 6px;
          background: linear-gradient(180deg, #4a3728 0%, #2d1f15 100%);
          border: 1px solid #5c4033;
          border-radius: 4px;
        }
        #selection-info .unit-item-icon {
          width: 24px;
          height: 24px;
          border-radius: 3px;
          overflow: hidden;
        }
        #selection-info .unit-item-icon img {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }
        #selection-info .unit-item-name {
          flex: 1;
          font-size: 11px;
          color: #c4a77d;
        }
        #selection-info .unit-item-count {
          font: 700 12px 'Noto Serif SC', 'SimSun', serif;
          color: #ffd35a;
        }

        /* Compact layout for small viewports (desktop 720p) */
        @media (max-height: 800px) {
          #selection-info {
            left: 10px;
            width: 170px;
            font-size: 11px;
          }
          #selection-info .panel-wrapper {
            border-width: 2px;
          }
          #selection-info .panel-content {
            padding: 8px;
          }
          #selection-info .info-icon {
            width: 38px;
            height: 38px;
          }
          #selection-info .info-name {
            font-size: 13px;
          }
          #selection-info .info-row {
            padding: 4px 6px;
            margin-bottom: 4px;
          }
          #selection-info .info-row-label {
            font-size: 10px;
          }
          #selection-info .info-row-value {
            font-size: 11px;
          }
          #selection-info .health-section {
            margin-bottom: 6px;
          }
          #selection-info .info-header {
            margin-bottom: 6px;
            padding-bottom: 6px;
          }
        }
      </style>
      <div class="panel-wrapper">
        <div class="corner tl"></div>
        <div class="corner tr"></div>
        <div class="corner bl"></div>
        <div class="corner br"></div>
        <div class="panel-title">
          <span class="panel-title-text"></span>
        </div>
        <div class="panel-content">
          <div class="selection-content"></div>
        </div>
      </div>
    `;

    document.body.appendChild(this.container);
    this.setupSubscription();
    this.setupEvents();
  }

  private setupEvents(): void {
    // Use pointerdown instead of click to avoid interference from game's pointer capture
    this.container.addEventListener('pointerdown', (e) => {
      const target = e.target as HTMLElement;

      const upgradeBtn = target.closest('.btn-upgrade') as HTMLElement;
      if (upgradeBtn && !upgradeBtn.hasAttribute('disabled')) {
        e.stopPropagation();
        const entityId = upgradeBtn.dataset.entity;
        if (entityId) {
          EventBus.emit('building:upgrade', { entityId: parseInt(entityId) });
        }
        return;
      }

      const repairBtn = target.closest('.btn-repair') as HTMLElement;
      if (repairBtn && !repairBtn.hasAttribute('disabled')) {
        e.stopPropagation();
        const entityId = repairBtn.dataset.entity;
        if (entityId) {
          EventBus.emit('building:repair', { entityId: parseInt(entityId) });
        }
        return;
      }
    });
  }

  setWorld(world: World): void {
    this.world = world;
  }

  private setupSubscription(): void {
    this.unsubscribe = useGameStore.subscribe((state) => {
      this.updateSelection(state.selectedEntities);
    });

    // Periodic refresh to update health/damage state for repair button
    this.refreshInterval = window.setInterval(() => {
      const entities = useGameStore.getState().selectedEntities;
      if (entities.length > 0) {
        this.updateSelection(entities);
      }
    }, 500);
  }

  private updateSelection(entities: Entity[]): void {
    if (!this.world || entities.length === 0) {
      this.container.classList.remove('visible');
      return;
    }

    this.container.classList.add('visible');
    const contentEl = this.container.querySelector('.selection-content');
    const titleEl = this.container.querySelector('.panel-title-text');
    if (!contentEl || !titleEl) return;

    // Determine panel type
    const firstEntity = entities[0];
    const building = this.world.getComponent<BuildingComponent>(firstEntity, ComponentTypes.Building);
    const unit = this.world.getComponent<UnitComponent>(firstEntity, ComponentTypes.Unit);

    if (entities.length === 1) {
      if (building) {
        titleEl.textContent = '建筑信息';
        contentEl.innerHTML = this.renderBuildingInfo(firstEntity);
      } else if (unit) {
        titleEl.textContent = '单位信息';
        contentEl.innerHTML = this.renderUnitInfo(firstEntity);
      }
    } else {
      titleEl.textContent = '军队信息';
      contentEl.innerHTML = this.renderMultiSelection(entities);
    }
  }

  private renderBuildingInfo(entity: Entity): string {
    if (!this.world) return '';

    const health = this.world.getComponent<HealthComponent>(entity, ComponentTypes.Health);
    const faction = this.world.getComponent<FactionComponent>(entity, ComponentTypes.Faction);
    const building = this.world.getComponent<BuildingComponent>(entity, ComponentTypes.Building);

    if (!health || !faction || !building) return '';

    const factionData = FACTIONS[faction.factionId];
    const buildingStats = BUILDING_STATS[building.buildingType];
    const healthPercent = (health.current / health.max) * 100;
    const isLowHealth = healthPercent < 30;
    const efficiency = getBuildingEfficiency(building.level);
    const canUpgrade = building.level < MAX_BUILDING_LEVEL;
    const upgradeCost = canUpgrade ? getUpgradeCost(buildingStats.cost, building.level) : 0;
    const playerFaction = useGameStore.getState().playerFaction;
    const isPlayerBuilding = faction.factionId === playerFaction;
    const damage = health.max - health.current;
    const needsRepair = damage > 0;
    const repairCost = needsRepair ? getRepairCost(damage) : 0;

    // Next level preview
    const nextEfficiency = canUpgrade ? getBuildingEfficiency(building.level + 1) : efficiency;
    const currentBonus = Math.round((efficiency - 1) * 100);
    const nextBonus = Math.round((nextEfficiency - 1) * 100);

    // Get building image
    const buildingImage = UI_IMAGES.buildings[building.buildingType as keyof typeof UI_IMAGES.buildings] || '';
    const buildingName = this.getBuildingName(building.buildingType);

    let html = `
      <div class="info-header">
        <div class="info-icon">
          ${buildingImage ? `<img src="${buildingImage}" alt="${buildingName}" />` : ''}
        </div>
        <div class="info-title">
          <div class="info-name">${buildingName}</div>
          <div class="info-faction">${factionData.name}</div>
        </div>
      </div>

      <div class="health-section">
        <div class="health-label">
          <span>耐久度</span>
          <span>${Math.floor(health.current)} / ${health.max}</span>
        </div>
        <div class="health-bar">
          <div class="health-bar-fill ${isLowHealth ? 'low' : ''}" style="width: ${healthPercent}%"></div>
        </div>
      </div>

      <div class="info-row">
        <span class="info-row-label">等级</span>
        <span class="info-row-value">${'★'.repeat(building.level)}${'☆'.repeat(MAX_BUILDING_LEVEL - building.level)}</span>
      </div>

      <div class="info-row">
        <span class="info-row-label">效率加成</span>
        <span class="info-row-value">+${currentBonus}%</span>
      </div>
    `;

    // Income info
    if (buildingStats.income) {
      const currentIncome = Math.round(buildingStats.income * efficiency);
      html += `
        <div class="info-row">
          <span class="info-row-label">金币产出</span>
          <span class="info-row-value">+${currentIncome}/周期</span>
        </div>
      `;
    }

    // Production info
    if (buildingStats.spawnType && buildingStats.spawnInterval) {
      const unitImage = UI_IMAGES.units[buildingStats.spawnType as keyof typeof UI_IMAGES.units] || '';
      const unitName = this.getUnitName(buildingStats.spawnType);
      const interval = Math.round(buildingStats.spawnInterval / efficiency);

      html += `
        <div class="production-info">
          <div class="production-icon">
            ${unitImage ? `<img src="${unitImage}" alt="${unitName}" />` : ''}
          </div>
          <div class="production-details">
            <div class="production-name">训练: ${unitName}</div>
            <div class="production-time">每 ${interval} 秒生产一个</div>
          </div>
        </div>
      `;
    }

    // Upgrade preview (for player buildings only)
    if (isPlayerBuilding && canUpgrade) {
      let previewHtml = `
        <div class="upgrade-preview">
          <div class="upgrade-preview-title">升级至 Lv.${building.level + 1} 后</div>
          <div class="upgrade-preview-row">
            <span>效率加成</span>
            <span class="upgrade-preview-new">+${currentBonus}% → +${nextBonus}%</span>
          </div>
      `;

      if (buildingStats.income) {
        const currentIncome = Math.round(buildingStats.income * efficiency);
        const nextIncome = Math.round(buildingStats.income * nextEfficiency);
        previewHtml += `
          <div class="upgrade-preview-row">
            <span>金币产出</span>
            <span class="upgrade-preview-new">+${currentIncome} → +${nextIncome}</span>
          </div>
        `;
      }

      if (buildingStats.spawnInterval) {
        const currentInterval = Math.round(buildingStats.spawnInterval / efficiency);
        const nextInterval = Math.round(buildingStats.spawnInterval / nextEfficiency);
        previewHtml += `
          <div class="upgrade-preview-row">
            <span>训练速度</span>
            <span class="upgrade-preview-new">${currentInterval}秒 → ${nextInterval}秒</span>
          </div>
        `;
      }

      previewHtml += `</div>`;
      html += previewHtml;
    }

    // Action buttons (player only)
    if (isPlayerBuilding) {
      html += `<div class="action-buttons">`;

      if (canUpgrade) {
        html += `
          <button class="btn btn-upgrade" data-entity="${entity}">
            升级 (${upgradeCost} 金)
          </button>
        `;
      } else {
        html += `<button class="btn btn-upgrade" disabled>已满级</button>`;
      }

      if (needsRepair) {
        html += `
          <button class="btn btn-repair" data-entity="${entity}">
            修复 (${repairCost} 金)
          </button>
        `;
      }

      html += `</div>`;
    }

    return html;
  }

  private renderUnitInfo(entity: Entity): string {
    if (!this.world) return '';

    const health = this.world.getComponent<HealthComponent>(entity, ComponentTypes.Health);
    const faction = this.world.getComponent<FactionComponent>(entity, ComponentTypes.Faction);
    const unit = this.world.getComponent<UnitComponent>(entity, ComponentTypes.Unit);

    if (!health || !faction || !unit) return '';

    const factionData = FACTIONS[faction.factionId];
    const unitStats = UNIT_STATS[unit.unitType];
    const healthPercent = (health.current / health.max) * 100;
    const isLowHealth = healthPercent < 30;

    const unitImage = UI_IMAGES.units[unit.unitType as keyof typeof UI_IMAGES.units] || '';
    const unitName = this.getUnitName(unit.unitType);

    return `
      <div class="info-header">
        <div class="info-icon">
          ${unitImage ? `<img src="${unitImage}" alt="${unitName}" />` : ''}
        </div>
        <div class="info-title">
          <div class="info-name">${unitName}</div>
          <div class="info-faction">${factionData.name}</div>
        </div>
      </div>

      <div class="health-section">
        <div class="health-label">
          <span>生命值</span>
          <span>${Math.floor(health.current)} / ${health.max}</span>
        </div>
        <div class="health-bar">
          <div class="health-bar-fill ${isLowHealth ? 'low' : ''}" style="width: ${healthPercent}%"></div>
        </div>
      </div>

      <div class="info-row">
        <span class="info-row-label">攻击力</span>
        <span class="info-row-value">${unitStats.attack}</span>
      </div>

      <div class="info-row">
        <span class="info-row-label">防御力</span>
        <span class="info-row-value">${unitStats.defense}</span>
      </div>

      <div class="info-row">
        <span class="info-row-label">移动速度</span>
        <span class="info-row-value">${unitStats.moveSpeed.toFixed(1)}</span>
      </div>

      <div class="info-row">
        <span class="info-row-label">攻击范围</span>
        <span class="info-row-value">${unitStats.range.toFixed(1)}</span>
      </div>
    `;
  }

  private renderMultiSelection(entities: Entity[]): string {
    if (!this.world) return '';

    // Count units by type
    const unitCounts: Record<string, number> = {};
    let totalHealth = 0;
    let maxHealth = 0;

    for (const entity of entities) {
      const health = this.world.getComponent<HealthComponent>(entity, ComponentTypes.Health);
      const unit = this.world.getComponent<UnitComponent>(entity, ComponentTypes.Unit);

      if (health) {
        totalHealth += health.current;
        maxHealth += health.max;
      }

      if (unit) {
        unitCounts[unit.unitType] = (unitCounts[unit.unitType] || 0) + 1;
      }
    }

    const healthPercent = maxHealth > 0 ? (totalHealth / maxHealth) * 100 : 0;
    const isLowHealth = healthPercent < 30;

    let html = `
      <div class="info-header">
        <div class="info-icon">
          <img src="/images/ui/icons/unit.webp" alt="军队" />
        </div>
        <div class="info-title">
          <div class="info-name">已选中 ${entities.length} 个单位</div>
          <div class="info-faction">我方军队</div>
        </div>
      </div>

      <div class="health-section">
        <div class="health-label">
          <span>总生命值</span>
          <span>${Math.floor(totalHealth)} / ${Math.floor(maxHealth)}</span>
        </div>
        <div class="health-bar">
          <div class="health-bar-fill ${isLowHealth ? 'low' : ''}" style="width: ${healthPercent}%"></div>
        </div>
      </div>

      <div class="unit-list">
    `;

    for (const [type, count] of Object.entries(unitCounts)) {
      const unitImage = UI_IMAGES.units[type as keyof typeof UI_IMAGES.units] || '';
      const unitName = this.getUnitName(type);

      html += `
        <div class="unit-item">
          <div class="unit-item-icon">
            ${unitImage ? `<img src="${unitImage}" alt="${unitName}" />` : ''}
          </div>
          <span class="unit-item-name">${unitName}</span>
          <span class="unit-item-count">x${count}</span>
        </div>
      `;
    }

    html += `</div>`;

    return html;
  }

  private getUnitName(type: string): string {
    const names: Record<string, string> = {
      sword: '剑士',
      archer: '弓兵',
      spear: '枪兵',
      cavalry: '骑兵',
      shield: '盾兵',
      taoist: '道士',
    };
    return names[type] || type;
  }

  private getBuildingName(type: string): string {
    const names: Record<string, string> = {
      capital: '王城',
      barracks: '兵营',
      tower: '箭塔',
      wall: '城墙',
      mine: '矿场',
      farm: '农场',
      market: '市场',
      stable: '马厩',
      smithy: '铁匠铺',
      temple: '道观',
      archery: '射箭场',
      spearcamp: '枪营',
    };
    return names[type] || type;
  }

  dispose(): void {
    if (this.unsubscribe) {
      this.unsubscribe();
    }
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
    this.container.remove();
  }
}
